import React, { useEffect, useState } from "react";
import { unionArray, useControlledPromise } from "scmp-sdk";
import { useSdkContext } from "scmp-sdk/components/contexts";
import type { TooltipItem } from "scmp-sdk/components/dashboard-widget/tooltips";

import { useFallbackStorageValue } from "shared/hooks";

import { PianoTooltipDisplay } from "./component";

export const usePianoTooltipRender = () => {
  const { rosettaInstance } = useSdkContext();
  const { set: setPianoTooltipHistory, value: pianoTooltipHistory } = useFallbackStorageValue<
    string[]
  >("pianoTooltipHistory", {
    defaultValue: [],
  });
  const { message, shortKey, version } =
    rosettaInstance?.rulesEngine.getOutcome("ui.tooltip.messageObject") ?? {};
  const tooltipKey = shortKey && version && `${shortKey}_${version.toString()}`;
  const [isNeedToShowRosettaTooltip, setIsNeedToShowRosettaTooltip] = useState<boolean>();

  const { promise, reject, resolve } = useControlledPromise<TooltipItem>();

  useEffect(() => {
    if (!rosettaInstance) return;
    else if (tooltipKey && pianoTooltipHistory) {
      setIsNeedToShowRosettaTooltip(!pianoTooltipHistory.includes(tooltipKey));
      return;
    }
    setIsNeedToShowRosettaTooltip(false);
  }, [pianoTooltipHistory, rosettaInstance, tooltipKey, version]);

  useEffect(() => {
    if (!rosettaInstance || isNeedToShowRosettaTooltip === undefined) return;
    else if (isNeedToShowRosettaTooltip && message && tooltipKey)
      return resolve.current({
        node: <PianoTooltipDisplay message={message} />,
        onChangeUrl: () => {
          setPianoTooltipHistory(pianoHistory => unionArray(pianoHistory ?? [], [tooltipKey]));
          setIsNeedToShowRosettaTooltip(false);
        },
        onClose: () => {
          setPianoTooltipHistory(pianoHistory => unionArray(pianoHistory ?? [], [tooltipKey]));
          setIsNeedToShowRosettaTooltip(false);
        },
      });
    return reject.current();
  }, [
    isNeedToShowRosettaTooltip,
    message,
    reject,
    resolve,
    rosettaInstance,
    setPianoTooltipHistory,
    tooltipKey,
  ]);

  return {
    pianoTooltipRender: promise,
  };
};
